import React, { useMemo, useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import Home from './components/Home';
import About from './components/About';
import Contact from './components/Contact';
import Login from './components/Login';

function AboutNavDropdown({ linkClassName, activeClassName, onNavClick, menuClassName }) {
  const location = useLocation();
  const dropdownRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const isAboutPage = location.pathname === '/about';
  const linkClass = isAboutPage ? `${linkClassName} ${activeClassName}` : linkClassName;

  const openMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
  };

  const closeMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(false);
  };

  const scheduleClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, 120);
  };

  const handleNavigate = (e, path) => {
    closeMenu();
    onNavClick(e, path);
  };

  useEffect(() => {
    closeMenu();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={`nav-dropdown ${menuClassName}${isOpen ? ' is-open' : ''}`}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocus={openMenu}
      onBlur={scheduleClose}
    >
      <Link
        to="/about"
        className={linkClass}
        onClick={(e) => handleNavigate(e, '/about')}
      >
        About
      </Link>
      <div className="nav-dropdown-menu" role="menu">
        <Link
          to="/about"
          className="nav-dropdown-item"
          role="menuitem"
          onClick={(e) => handleNavigate(e, '/about')}
        >
          About Us
        </Link>
        <Link
          to="/about?view=pricing"
          className="nav-dropdown-item"
          role="menuitem"
          onClick={(e) => handleNavigate(e, '/about?view=pricing')}
        >
          Pricing
        </Link>
      </div>
    </div>
  );
}

function Navigation({ isVisible, onNavClick }) {
  const location = useLocation();
  
  return (
    <nav className={`navigation ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="nav-container">
        <div className="nav-brand">
          <Link to="/" className="logo-link" onClick={(e) => onNavClick(e, '/')}>
            <img src="/empy_svg_test.svg" alt="Empathos" className="logo-svg" />
          </Link>
        </div>
        <div className="nav-links">
          <Link 
            to="/" 
            className={location.pathname === '/' ? 'nav-link active' : 'nav-link'}
            onClick={(e) => onNavClick(e, '/')}
          >
            Home
          </Link>
          <AboutNavDropdown
            linkClassName="nav-link"
            activeClassName="active"
            onNavClick={onNavClick}
            menuClassName="nav-dropdown--sticky"
          />
          <Link 
            to="/contact" 
            className={location.pathname === '/contact' ? 'nav-link active' : 'nav-link'}
            onClick={(e) => onNavClick(e, '/contact')}
          >
            Contact
          </Link>
          <Link 
            to="/login" 
            className={location.pathname === '/login' ? 'nav-link active' : 'nav-link'}
            onClick={(e) => onNavClick(e, '/login')}
          >
            Login
          </Link>
        </div>
      </div>
    </nav>
  );
}

function HeroBanner() {
  const location = useLocation();
  const boxSize = 16;
  const navigate = useNavigate();

  // Smooth scroll to main content
  const scrollToContent = () => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Handle navigation with scroll
  const handleNavClick = (e, path) => {
    e.preventDefault(); // Prevent default Link behavior
    // Navigate first
    navigate(path);
    // Then scroll after a brief delay to allow navigation
    setTimeout(() => {
      // For all pages, scroll to top of main content
      scrollToContent();
    }, 100);
  };

  // Generate pattern of boxes with varying density (for hero banner only)
  const generateBoxPattern = () => {
    const colors = [
      'rgba(255, 223, 128, var(--opacity))', // yellow
      'rgba(255, 179, 128, var(--opacity))', // peach
      'rgba(198, 164, 207, var(--opacity))', // purple
      'rgba(156, 175, 207, var(--opacity))', // blue
      'rgba(164, 207, 176, var(--opacity))', // green
    ];
    
    // Add extra columns and rows to ensure coverage
    const numCols = Math.ceil(window.innerWidth / boxSize) + 2;
    const numRows = Math.ceil(window.innerHeight / boxSize) + 2;
    
    const boxes = [];
    
    for (let i = 0; i < numCols; i++) {
      for (let j = 0; j < numRows; j++) {
        // Calculate probability based on x position (increases from left to right)
        const probability = Math.min(0.01 + Math.pow(i / numCols, 2) * 0.4, 0.3);
        
        if (Math.random() < probability) {
          const colorIndex = Math.floor(Math.random() * colors.length);
          const initialDelay = Math.random() * 2; // Short initial delay (0-2s)
          const cycleDelay = Math.random() * 60; // Keep the longer subsequent delays (0-60s)
          const duration = 30 + Math.random() * 60; // Keep the longer duration (30-90s)
          const startOpacity = 0.1 + Math.random() * 0.1;
          const peakOpacity = 0.2 + Math.random() * 0.15;
          
          boxes.push({
            x: i * boxSize,
            y: j * boxSize,
            color: colors[colorIndex],
            initialDelay,
            cycleDelay,
            duration,
            startOpacity,
            peakOpacity,
          });
        }
      }
    }
    
    return boxes;
  };

  const boxPattern = useMemo(() => generateBoxPattern(), []);

  // Create the dynamic CSS styles for hero banner
  const createDynamicStyles = () => {
    const opacityVariables = {};
    const keyframes = {};
    
    // Create CSS custom properties for each box
    Array.from({ length: 1001 }, (_, i) => {
      opacityVariables[`--opacity-${i}`] = '0.1';
    });

    // Create individual keyframes for each box
    boxPattern.forEach(({ initialDelay, startOpacity, peakOpacity }) => {
      const keyframeName = `pulse-${Math.floor(initialDelay * 100)}`;
      keyframes[keyframeName] = `
        @keyframes ${keyframeName} {
          0% { --opacity-${Math.floor(initialDelay * 100)}: 0.1; }
          10% { --opacity-${Math.floor(initialDelay * 100)}: ${peakOpacity}; }
          20% { --opacity-${Math.floor(initialDelay * 100)}: ${startOpacity}; }
          100% { --opacity-${Math.floor(initialDelay * 100)}: ${startOpacity}; }
        }
      `;
    });

    return { opacityVariables, keyframes };
  };

  const { opacityVariables, keyframes } = useMemo(() => createDynamicStyles(), [boxPattern]);

  // Create the background gradients
  const backgroundGradients = boxPattern.map(({ x, y, color, initialDelay }) => 
    `linear-gradient(${color.replace('var(--opacity)', `var(--opacity-${Math.floor(initialDelay * 100)})`)} 0%, 
     ${color.replace('var(--opacity)', `var(--opacity-${Math.floor(initialDelay * 100)})`)} 100%) 
     ${x}px ${y}px / ${boxSize}px ${boxSize}px no-repeat`
  ).join(',');

  // Create the animation string
  const animationString = boxPattern.map(({ initialDelay, cycleDelay, duration }) => 
    `pulse-${Math.floor(initialDelay * 100)} ${duration}s ${cycleDelay}s infinite ease-in-out`
  ).join(',');
  
  return (
    <div 
      className="hero-banner"
      style={{
        position: 'relative',
        ...opacityVariables,
      }}
    >
      {/* Inject the dynamic keyframes for hero banner */}
      <style>
        {Object.values(keyframes).join('\n')}
      </style>
      
      {/* Animated background layer for hero banner only */}
      <div 
        className="hero-animated-background"
        style={{
          position: 'absolute',
          top: '-20px',
          left: '-20px',
          right: '-20px',
          bottom: '-20px',
          background: backgroundGradients,
          animation: animationString,
          pointerEvents: 'none',
          opacity: 0.9,
          zIndex: 0,
        }}
      />

      {/* Glossy overlay filter */}
      <div className="hero-glass-overlay" />
      
      <div className="hero-content">
        <div className="hero-logo">
          <img src="/empy-logo.png" alt="Empathos" className="hero-logo-image" />
        </div>
        <div className="hero-navigation">
          <Link 
            to="/" 
            className="hero-nav-link"
            onClick={(e) => handleNavClick(e, '/')}
          >
            Home
          </Link>
          <AboutNavDropdown
            linkClassName="hero-nav-link"
            activeClassName=""
            onNavClick={handleNavClick}
            menuClassName="nav-dropdown--hero"
          />
          <Link 
            to="/contact" 
            className="hero-nav-link"
            onClick={(e) => handleNavClick(e, '/contact')}
          >
            Contact
          </Link>
          <Link 
            to="/login" 
            className="hero-nav-link"
            onClick={(e) => handleNavClick(e, '/login')}
          >
            Login
          </Link>
        </div>
      </div>
      
      {/* Scroll indicator - moved outside hero-content for proper centering */}
      <div className="scroll-indicator-container">
        <div className="scroll-indicator" onClick={scrollToContent}>
          <div className="scroll-chevron">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScrollHandler({ children, onNavClick }) {
  const [showStickyNav, setShowStickyNav] = useState(false);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const heroBanner = document.querySelector('.hero-banner');
          if (heroBanner) {
            const heroBottom = heroBanner.offsetTop + heroBanner.offsetHeight;
            const scrollPosition = window.scrollY;
            
            // Show sticky nav when hero banner is out of view
            setShowStickyNav(scrollPosition > heroBottom - 100);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Navigation isVisible={showStickyNav} onNavClick={onNavClick} />
      {children}
    </>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Handle hash-based scrolling (#form)
  useEffect(() => {
    // Check if URL has #form hash
    if (location.hash === '#form') {
      // Scroll to main content after a brief delay to allow page render
      setTimeout(() => {
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
          mainContent.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    // If no hash, let default browser behavior handle scrolling (top of page)
  }, [location.pathname, location.hash]);
  
  // Create a shared navigation handler
  const handleNavClick = (e, path) => {
    e.preventDefault(); // Prevent default Link behavior
    // Navigate first
    navigate(path);
    // Then scroll after a brief delay to allow navigation
    setTimeout(() => {
      // For all pages, scroll to top of main content
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        mainContent.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  return (
    <div className="app">
      {/* Simple blue background for the entire app */}
      <div className="app-background" />
      
      {/* Scrollable content */}
      <div className="scrollable-content">
        <ScrollHandler onNavClick={handleNavClick}>
          <HeroBanner />
          <main className="main-content" id="form">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<Login />} />
            </Routes>
          </main>
        </ScrollHandler>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
